import type { ReactNode } from 'react';
import type { VariantesDeSku } from '../lib/variantes';

/**
 * Stock de un SKU con las mismas etiquetas en todas las pantallas (backend: ResumenStockSku, stock-bom.calculo):
 * - Stock físico: total en el depósito (incluye lo reservado por ventas sin despachar: sigue en el estante).
 * - Stock reservado: reservado por ventas.
 * - Stock disponible: físico − reservado − cuarentena (nunca negativo).
 * - En cuarentena (sólo si es > 0): devoluciones con falla, en el depósito pero no se venden ni se publican.
 * - Se publica (opcional): lo que escribe la sincronización por SKU (disponible − reserva de estante).
 * Packs/combos no tienen stock propio: los números van en packs, calculados desde sus componentes.
 * Un SKU sin contar (contado === false: todavía no tiene un conteo; un pack, si le falta a alguno de sus componentes)
 * muestra "Sin contar" en lugar del físico y el disponible: su stock no está declarado. El reservado se muestra igual.
 * Contado con Stock físico negativo (las ventas superaron lo declarado): el número en rojo, con la explicación.
 */

export interface ResumenStockSku {
  fisico: number | null;
  reservado: number | null;
  /** Devoluciones con falla (sólo viene si es > 0). */
  cuarentena?: number;
  disponible: number | null;
  sePublica?: number | null;
  esCombo: boolean;
  limitante: string | null;
  motivo: string | null;
  /** Tiene un conteo (packs/combos: todos sus componentes). Sin el dato, se muestran los números. */
  contado?: boolean;
}

export const ETIQUETAS_STOCK = {
  fisico: 'Stock físico',
  reservado: 'Stock reservado',
  disponible: 'Stock disponible',
  sePublica: 'Se publica',
} as const;

export const formatoStock = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('es-AR'));

export const ETIQUETA_CUARENTENA = 'En cuarentena';
export const AYUDA_CUARENTENA = 'Volvieron con falla: están en el depósito pero no se venden ni se publican.';

export const SIN_CONTAR = 'Sin contar';
export const AYUDA_SIN_CONTAR = 'Este SKU todavía no tiene un conteo: su stock no está declarado.';
/** Stock físico negativo: las ventas (que siempre bajan el depósito) superaron lo declarado. */
export const AYUDA_FISICO_NEGATIVO = 'Se vendió más de lo declarado: revisá con un conteo.';

type Campo = keyof typeof ETIQUETAS_STOCK;

/** Campos que dependen de lo declarado: sin contar, no se muestran. */
const CAMPOS_DECLARADOS: ReadonlySet<Campo> = new Set<Campo>(['fisico', 'disponible']);

interface Props {
  stock: ResumenStockSku | null | undefined;
  /** 'bloque': en una línea, para encabezados; 'celda': una línea por número, para tablas. */
  variante?: 'bloque' | 'celda';
  mostrarSePublica?: boolean;
  /** Bloque: lo que sigue en la misma línea después de los números (p. ej. la reserva de estante). */
  despues?: ReactNode;
  className?: string;
  /** Desglose por variante del físico (una línea por variante y «Sin asignar»). */
  variantes?: Pick<VariantesDeSku, 'variantes' | 'sinAsignar'> | null;
}

const notaCombo = (s: ResumenStockSku) => (s.limitante ? `en packs, limita ${s.limitante}` : 'en packs');

export function StockSku({ stock, variante = 'bloque', mostrarSePublica = false, despues, className = '', variantes }: Props) {
  if (!stock) return <span className="text-slate-400">—</span>;

  const campos: Campo[] = mostrarSePublica ? ['fisico', 'reservado', 'disponible', 'sePublica'] : ['fisico', 'reservado', 'disponible'];
  const valor = (c: Campo) => {
    if (stock.contado === false && CAMPOS_DECLARADOS.has(c)) {
      return <strong title={AYUDA_SIN_CONTAR} className="text-amber-700">{SIN_CONTAR}</strong>;
    }
    const v = stock[c];
    if (v == null) return <span title={stock.motivo ?? undefined} className="text-slate-400">—</span>;
    if (c === 'fisico' && v < 0) return <strong title={AYUDA_FISICO_NEGATIVO} className="text-red-700">{formatoStock(v)}</strong>;
    return <strong className="text-slate-800">{formatoStock(v)}</strong>;
  };
  const nota = stock.esCombo && stock.motivo === null
    ? <span className="text-xs text-slate-500">{notaCombo(stock)}</span>
    : null;
  const cuarentena = (stock.cuarentena ?? 0) > 0
    ? <><span>{`${ETIQUETA_CUARENTENA}:`}</span> <strong className="text-amber-700">{formatoStock(stock.cuarentena)}</strong></>
    : null;

  if (variante === 'celda') {
    return (
      <div className={`text-xs text-slate-600 space-y-0.5 ${className}`}>
        {campos.map((c) => (
          <div key={c} className="whitespace-nowrap">
            <span>{`${ETIQUETAS_STOCK[c]}:`}</span> {valor(c)}
          </div>
        ))}
        {cuarentena && <div className="whitespace-nowrap" title={AYUDA_CUARENTENA}>{cuarentena}</div>}
        {nota && <div>{nota}</div>}
      </div>
    );
  }

  const desglose = variantes && variantes.variantes.length > 0 && (
    <div className="mt-1 space-y-0.5 text-xs text-slate-600">
      {variantes.variantes.map((v) => (
        <div key={v.id} className="flex items-center gap-1.5">
          <span aria-hidden className="inline-block h-3 w-3 rounded-sm border border-slate-300" style={{ backgroundColor: v.colorHex ?? 'transparent' }} />
          <span>{v.nombre}</span>: <strong className="text-slate-800">{formatoStock(v.cantidad)}</strong>
        </div>
      ))}
      <div><span>Sin asignar</span>: <strong className="text-slate-800">{formatoStock(variantes.sinAsignar)}</strong></div>
    </div>
  );

  return (
    <div className={className}>
    <p className="text-sm text-slate-700">
      {campos.map((c, i) => (
        <span key={c}>
          {i > 0 && ' · '}
          <span>{`${ETIQUETAS_STOCK[c]}:`}</span> {valor(c)}
        </span>
      ))}
      {cuarentena && <span title={AYUDA_CUARENTENA}>{' · '}{cuarentena}</span>}
      {despues}
      {nota && <> {nota}</>}
    </p>
    {desglose}
    </div>
  );
}
